// How an approval card reads a raw tool argument, per NCL display type. Pure,
// so the card component only lays the results out.

import type {
  ConfigCodeView,
  ConfigRowsPreview,
} from "@/types/api/spotflow/spotflow-config";
import { isRecord } from "@/utils/isRecord";
import type { EntityKind } from "./approvalView";
import { spotFileId } from "@/utils/generic";
import { formatTableDatetime } from "@/utils/formatTableDatetime";

export type CodeLanguage = NonNullable<ConfigCodeView["language"]>;

export const PREVIEW_ROWS = 3;

const ACRONYMS = new Set(["kb", "llm", "sql", "api", "id", "url"]);

export function humanize(key: string): string {
  const text = key
    .replace(/_v\d+$/, "")
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => (ACRONYMS.has(w.toLowerCase()) ? w.toUpperCase() : w))
    .join(" ");
  return text.charAt(0).toUpperCase() + text.slice(1);
}

export function plural(n: number, noun: string): string {
  return `${n} ${noun}${n === 1 ? "" : "s"}`;
}

// An arg the call did not pass, or passed empty, renders nothing.
export function isEmptyValue(v: unknown): boolean {
  if (v === undefined || v === null || v === "") return true;
  if (Array.isArray(v)) return v.length === 0;
  return isRecord(v) && Object.keys(v).length === 0;
}

// A cell's value as the card draws it: a file object reads as its uri, which
// the card then shows as the file; anything else as formatValue reads it.
export function cellText(v: unknown): string | undefined {
  if (isRecord(v) && typeof v.uri === "string" && spotFileId(v.uri)) {
    return v.uri;
  }
  return formatValue(v);
}

export function formatValue(v: unknown): string | undefined {
  if (v === undefined || v === null || v === "") return undefined;
  if (typeof v === "boolean") return v ? "Yes" : "No";
  if (typeof v === "string" || typeof v === "number") return String(v);
  if (Array.isArray(v)) {
    if (v.every((x) => typeof x === "string" || typeof x === "number")) {
      return v.join(", ");
    }
    return plural(v.length, "item");
  }
  if (isRecord(v)) {
    const parts = Object.entries(v)
      .map(([k, x]) => [k, formatValue(x)] as const)
      .filter(([, x]) => x !== undefined)
      .map(([k, x]) => `${humanize(k)}: ${x}`);
    return parts.length > 0 ? parts.join(", ") : undefined;
  }
  return undefined;
}

// A select's value reads as its option's label; a value the options do not
// list reads humanized rather than raw.
export function choiceLabel(values: unknown, v: unknown): string | undefined {
  const option = Array.isArray(values)
    ? values.find((o) => isRecord(o) && o.value === v)
    : undefined;
  if (isRecord(option) && typeof option.label === "string") return option.label;
  return typeof v === "string" ? humanize(v) : formatValue(v);
}

// What a choice does, when its options say (a repair's impact, a method's
// description).
export function choiceDescription(
  values: unknown,
  v: unknown
): string | undefined {
  const option = Array.isArray(values)
    ? values.find((o) => isRecord(o) && o.value === v)
    : undefined;
  return isRecord(option) && typeof option.description === "string"
    ? option.description
    : undefined;
}

export function quoteText(v: unknown): string | undefined {
  const parts = Array.isArray(v) ? v : [v];
  const text = parts.filter((p) => typeof p === "string" && p).join("\n\n");
  return text || undefined;
}

const EXTENSION_LANGUAGE: Record<string, CodeLanguage> = {
  py: "python",
  js: "javascript",
  mjs: "javascript",
  ts: "tsx",
  tsx: "tsx",
  json: "json",
  yaml: "yaml",
  yml: "yaml",
  html: "html",
  css: "css",
  sh: "bash",
  sql: "sql",
};

export function codeLanguage(
  display: ConfigCodeView,
  values: Record<string, unknown>
): CodeLanguage {
  const path = display.path_field ? values[display.path_field] : undefined;
  if (typeof path === "string" && path) {
    const ext = path.split(".").pop()?.toLowerCase() ?? "";
    return EXTENSION_LANGUAGE[ext] ?? "markdown";
  }
  return display.language ?? "markdown";
}

export interface RowsPreview {
  columns: string[];
  rows: string[][];
  // Each row's id in its table, when the call names existing rows.
  rowIds?: string[];
  // Per cell: the call sets it empty (a change that clears a value), as
  // opposed to not touching it.
  cleared?: boolean[][];
  // Rows the call covers; more than ``rows`` when only some were read back.
  total: number;
  // Per column: the table a reference column links to, so a cell reads as
  // the linked row rather than its id.
  refTables?: (string | undefined)[];
  // Per column: the kind of entity its cells are ids of, read as its name.
  entityKinds?: (EntityKind | undefined)[];
  // Per cell of a change: what the row holds now, read from its table.
  before?: (string | undefined)[][];
}

// A date reads as the grid shows it, not as the stored timestamp.
function tableCell(type: string | undefined, text: string): string {
  return (type === "datetime" && text && formatTableDatetime(text)) || text;
}

/** A preview keyed by a table's column names, headed by their labels as the
 * table shows them, with each reference column's linked table and each date
 * as the grid shows it. */
export function withTableColumns(
  preview: RowsPreview,
  columns: {
    name: string;
    label?: string | null;
    type?: string;
    references?: string | null;
  }[]
): RowsPreview {
  const byName = new Map(columns.map((c) => [c.name, c]));
  const types = preview.columns.map((h) => byName.get(h)?.type);
  return {
    ...preview,
    rows: preview.rows.map((row) =>
      row.map((cell, i) => tableCell(types[i], cell))
    ),
    before: preview.before?.map((row) =>
      row.map((cell, i) => (cell ? tableCell(types[i], cell) : cell))
    ),
    columns: preview.columns.map((h) => byName.get(h)?.label || h),
    refTables: preview.columns.map((h) => {
      const column = byName.get(h);
      return column?.type === "reference" && column.references
        ? column.references
        : undefined;
    }),
  };
}

// Rows a looked-up preview reads back: enough to expand, not a whole table.
export const LOOKUP_ROWS = 50;

const TITLE_LIKE = [
  /^(name|full[_ ]?name|title|label|subject)$|[_ ]name$/i,
  /^e-?mail$/i,
];

// The column a row reads as in a heading: a name-like one, then an email,
// else the first.
export function titleColumn(columns: string[]): number {
  for (const pattern of TITLE_LIKE) {
    const i = columns.findIndex((c) => pattern.test(c.trim()));
    if (i !== -1) return i;
  }
  return 0;
}

// Rows read back from their table, headed by the table's column labels.
export function lookedUpRows(
  columns: { name: string; label?: string | null; type?: string }[],
  rows: Record<string, unknown>[],
  requested: number
): RowsPreview | null {
  if (rows.length === 0) return null;
  const cell = (c: (typeof columns)[number], v: unknown) =>
    tableCell(c.type, cellText(v) ?? "");
  return {
    columns: columns.map((c) => c.label || c.name),
    rows: rows.map((r) => columns.map((c) => cell(c, r[c.name]))),
    total: Math.max(requested, rows.length),
  };
}

export function previewRows(
  display: ConfigRowsPreview,
  v: unknown
): RowsPreview | null {
  const items = (Array.isArray(v) ? v : []).filter(isRecord);
  const records = items
    .map((r) => (display.values_key ? r[display.values_key] : r))
    .filter(isRecord);
  if (records.length === 0) return null;
  const columns: [string, string[]][] = display.columns
    ? display.columns.filter(([, keys]) =>
        records.some((r) => keys.some((k) => !isEmptyValue(r[k])))
      )
    : [...new Set(records.flatMap((r) => Object.keys(r)))].map((key) => [
        key,
        [key],
      ]);
  const rowKey = display.row_key;
  const rowIds = rowKey
    ? items
        .map((r) => r[rowKey])
        .filter(
          (id): id is string | number =>
            typeof id === "string" || typeof id === "number"
        )
    : [];
  return {
    columns: columns.map(([header]) => header),
    rows: records.map((r) =>
      columns.map(([, keys]) => {
        const key = keys.find((k) => !isEmptyValue(r[k]));
        if (key === undefined) return "";
        const value = r[key];
        return display.labels?.[key]?.[String(value)] ?? cellText(value) ?? "";
      })
    ),
    rowIds: rowIds.length === records.length ? rowIds.map(String) : undefined,
    cleared: records.map((r) =>
      columns.map(
        ([, keys]) =>
          keys.some((k) => k in r) && keys.every((k) => isEmptyValue(r[k]))
      )
    ),
    total: records.length,
    entityKinds: columns.map(([, keys]) => {
      const key = keys.find((k) => display.entities?.[k]);
      return key ? display.entities?.[key] : undefined;
    }),
  };
}
